import React, { useState } from "react";
import { Button } from "./ui/button";
import { Download, ShieldOff, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function GdprPanel() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/auth/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sanmarinomarket-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export completato", { description: "I tuoi dati sono stati scaricati in formato JSON" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    const ok1 = window.confirm(
      "Sei sicuro di voler eliminare definitivamente il tuo account?\n\nVerranno cancellati: profilo, annunci, aste, offerte, baratti, messaggi, notifiche.\n\nQuesta operazione NON può essere annullata."
    );
    if (!ok1) return;
    const confirm = window.prompt('Per conferma, scrivi ELIMINA (in maiuscolo):');
    if (confirm !== "ELIMINA") {
      toast.info("Cancellazione annullata");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success("Account eliminato", { description: "Tutti i tuoi dati sono stati cancellati" });
      logout();
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la cancellazione");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="gdpr-panel">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-[#1d3d3a] text-white flex items-center justify-center">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-black text-[#1d3d3a]">Dati e Privacy</h3>
          <p className="text-xs text-slate-500">I tuoi diritti GDPR — Art. 15, 17, 20</p>
        </div>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Puoi <strong>scaricare</strong> in qualsiasi momento un export completo dei tuoi dati,
        oppure <strong>eliminare definitivamente</strong> il tuo account e tutto ciò che hai pubblicato.
      </p>

      <div className="grid sm:grid-cols-2 gap-2 mt-4">
        <Button
          onClick={handleExport}
          disabled={exporting}
          variant="outline"
          className="rounded-xl border-2 h-11 font-semibold"
          data-testid="gdpr-export-button"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Scarica i miei dati
        </Button>
        <Button
          onClick={handleDelete}
          disabled={deleting}
          variant="outline"
          className="rounded-xl border-2 border-red-200 text-red-700 hover:bg-red-50 h-11 font-semibold"
          data-testid="gdpr-delete-button"
        >
          {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldOff className="w-4 h-4 mr-2" />}
          Elimina account
        </Button>
      </div>
    </div>
  );
}
